import { useState } from "react";
import { toast } from "sonner";
import { LocateFixed, MessageCircle, Phone } from "lucide-react";
import { PageShell } from "@/components/site/PageShell";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { api, errMsg } from "@/lib/api";
import { PHONE, WHATSAPP } from "@/lib/constants";

const TYPES = ["Won't start", "Flat tyre", "Fuel out", "Collision", "Other"];

export default function Roadside() {
  const [f, setF] = useState({ name: "", email: "", phone: "", location: "", breakdown_type: TYPES[0], mobility: "Drivable", vehicle: "", notes: "" });
  const [busy, setBusy] = useState(false);
  const [ref, setRef] = useState(null);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const locate = () => {
    if (!navigator.geolocation) return toast.error("Location is not available on this device");
    navigator.geolocation.getCurrentPosition(
      (p) => setF((x) => ({ ...x, location: `GPS ${p.coords.latitude.toFixed(5)}, ${p.coords.longitude.toFixed(5)}` })),
      () => toast.error("Could not get your location — please type it"));
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.post("/public/roadside", f);
      setRef(r.data.reference);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <PageShell testId="roadside-page" overline="Roadside help" title="Stuck? Call us first." intro="The fastest way to get help is a phone call. You can also send your details below and we'll call you back.">
      <div className="grid lg:grid-cols-2 gap-8">
        <div className="space-y-4">
          <a href={`tel:${PHONE}`} data-testid="roadside-call-button" className="flex items-center justify-center gap-3 rounded-2xl bg-[#F86900] text-[#0D141A] font-display font-extrabold text-2xl md:text-3xl py-8 hover:bg-[#ff7d1f] transition-colors am-pulse">
            <Phone className="h-7 w-7" /> {PHONE}
          </a>
          <a href={WHATSAPP} target="_blank" rel="noreferrer" data-testid="roadside-whatsapp-button" className="flex items-center justify-center gap-2 rounded-2xl bg-[#020165] text-white font-semibold py-4 hover:bg-[#0a0a8a] transition-colors">
            <MessageCircle className="h-5 w-5" /> WhatsApp the workshop
          </a>
          <div className="rounded-2xl bg-white p-5 text-sm text-[#56585E]">
            <p className="font-semibold text-[#1D1E20]">Good to know</p>
            <p className="mt-1">Towing, jump-starts and tyre help are priced by distance and time of day (sample range USD 35–180). Help is only confirmed once a staff member calls you back.</p>
          </div>
        </div>
        <form onSubmit={submit} className="rounded-2xl bg-white p-6 space-y-3" data-testid="roadside-form">
          {ref ? (
            <div data-testid="roadside-success"><h2 className="font-display text-2xl font-bold">Request received</h2><p className="mt-2 text-[#56585E]">Reference <strong>{ref}</strong>. Keep your phone close — we'll call you back. If it's urgent, call {PHONE} now.</p></div>
          ) : (
            <>
              <h2 className="font-display font-bold text-xl">Request a call-back</h2>
              <div className="grid sm:grid-cols-2 gap-3">
                <Input required placeholder="Name" value={f.name} onChange={set("name")} data-testid="roadside-name-input" />
                <Input required placeholder="Phone" value={f.phone} onChange={set("phone")} data-testid="roadside-phone-input" />
              </div>
              <Input required type="email" placeholder="Email" value={f.email} onChange={set("email")} data-testid="roadside-email-input" />
              <div className="flex gap-2">
                <Input required placeholder="Where are you? (landmark or GPS)" value={f.location} onChange={set("location")} data-testid="roadside-location-input" />
                <button type="button" onClick={locate} data-testid="roadside-gps-button" className="shrink-0 rounded-md border px-3 hover:bg-black/5" aria-label="Use my location"><LocateFixed className="h-4 w-4" /></button>
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <select value={f.breakdown_type} onChange={set("breakdown_type")} data-testid="roadside-type-select" className="rounded-md border px-3 py-2 text-sm bg-white">{TYPES.map((t) => <option key={t}>{t}</option>)}</select>
                <select value={f.mobility} onChange={set("mobility")} data-testid="roadside-mobility-select" className="rounded-md border px-3 py-2 text-sm bg-white"><option>Drivable</option><option>Not drivable - needs recovery</option></select>
              </div>
              <Input placeholder="Vehicle (e.g. Toyota Hilux)" value={f.vehicle} onChange={set("vehicle")} data-testid="roadside-vehicle-input" />
              <Textarea rows={3} placeholder="Anything else?" value={f.notes} onChange={set("notes")} data-testid="roadside-notes-input" />
              <button disabled={busy} data-testid="roadside-submit-button" className="w-full rounded-full bg-[#020165] text-white font-semibold py-3 hover:bg-[#0a0a8a] disabled:opacity-60 transition-colors">{busy ? "Sending…" : "Send request"}</button>
            </>
          )}
        </form>
      </div>
    </PageShell>
  );
}
